/**
 * Shared Collect&Go automation-tab lifecycle for enrich + basket.
 * Opens when work needs the tab; after all holders release, waits idle then
 * shows a close countdown (cancellable) and closes Menu-owned tabs only.
 */

import {
  cogoHideCloseCountdown,
  cogoShowCloseCountdown,
  isCogoTabBusy,
} from './cogoExtension'

/** Quiet period with no automation holders before showing the close countdown. */
export const COGO_TAB_IDLE_MS = 10_000
/** Countdown overlay duration on the Collect&Go page. */
export const COGO_TAB_COUNTDOWN_SEC = 10

const holders = new Set<string>()
let idleTimer: ReturnType<typeof setTimeout> | null = null
let closeInFlight: Promise<void> | null = null
let generation = 0

function clearIdleTimer() {
  if (idleTimer != null) {
    clearTimeout(idleTimer)
    idleTimer = null
  }
}

async function runIdleClose(gen: number) {
  if (gen !== generation) return
  if (holders.size > 0 || isCogoTabBusy()) return

  try {
    const result = await cogoShowCloseCountdown(COGO_TAB_COUNTDOWN_SEC)
    if (gen !== generation || holders.size > 0 || isCogoTabBusy()) {
      void cogoHideCloseCountdown()
      return
    }
    if (result.cancelled) return
    // Extension closes the Menu-owned tab when countdown finishes.
  } catch {
    /* ignore — tab may already be gone */
  }
}

function scheduleIdleClose() {
  clearIdleTimer()
  if (holders.size > 0) return
  const gen = generation
  idleTimer = setTimeout(() => {
    idleTimer = null
    if (gen !== generation || holders.size > 0 || isCogoTabBusy()) return
    closeInFlight = runIdleClose(gen).finally(() => {
      closeInFlight = null
    })
  }, COGO_TAB_IDLE_MS)
}

/** Automation is using (or about to use) the Collect&Go tab. */
export function beginCogoAutomation(reason: string) {
  generation++
  clearIdleTimer()
  holders.add(reason)
  void cogoHideCloseCountdown()
}

/** Automation no longer needs the tab (queue empty / paused without tab). */
export function endCogoAutomation(reason: string) {
  holders.delete(reason)
  if (holders.size === 0) scheduleIdleClose()
}

export function cogoAutomationHolders(): string[] {
  return [...holders]
}

/** Test helper / emergency reset. */
export function resetCogoTabLifecycle() {
  generation++
  clearIdleTimer()
  holders.clear()
  closeInFlight = null
}
